import {
  Banner,
  Button,
  Card,
  EditIcon,
  AlertIcon,
  Flex,
  InfoIcon,
  Modal,
  Skeleton,
  Tag,
  Typography,
} from '@iheartjane/reefer';
import { Form, useFormContext } from '@iheartjane/reefer-hook-form';
import { pluralize } from '@iheartjane/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useStores } from '@jane/business-admin/data-access';
import type { GlobalSpecialStore } from '@jane/business-admin/types';
import { MODAL_CARD_WIDTH, concatAddress } from '@jane/business-admin/util';

import {
  SUBMIT_BUTTON_VARIANTS,
  StoreSelectModal,
} from './store_select/StoreSelectModal';

const StoreRow = ({
  handleChange,
  index,
  isChecked,
  isDisabled,
  isLast = false,
  store,
  storeAddress,
}: {
  handleChange: (checked: boolean) => void;
  index: number;
  isChecked?: boolean;
  isDisabled?: boolean;
  isLast?: boolean;
  store: GlobalSpecialStore;
  storeAddress: string;
}) => {
  const tagProps = store.recreational
    ? ({
        label: 'Rec',
        color: 'primary-dark',
        background: 'primary-light',
      } as const)
    : ({
        label: 'Med',
        color: 'palm-dark',
        background: 'palm-light',
      } as const);

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center">
        <Flex my={16} alignItems="center" gap={24}>
          <Tag {...tagProps} />
          <div>
            <Typography variant="body-semibold">{store.name}</Typography>
            <Typography color="grays-mid">{storeAddress}</Typography>
          </div>
        </Flex>
        <Flex gap={16} mx={8}>
          <Form.SwitchField
            label={`stores.${index}.enabled`}
            labelHidden
            name={`stores.${index}.enabled`}
            defaultChecked={isChecked}
            checked={isChecked}
            onChange={handleChange}
            shouldUnregister={false}
            disabled={isDisabled}
          />
          <Typography>{`${isChecked ? 'Enabled' : 'Disabled'}`}</Typography>
        </Flex>
      </Flex>
      {!isLast && <Modal.ContentDivider padding={false} />}
    </>
  );
};

const StoreSelectorSkeleton = () => {
  return (
    <Skeleton animate>
      <Skeleton.Bone height={48} mt={40} mb={16} />
      {Array.from({ length: 8 }, (_, i) => (
        <Skeleton.Bone key={`skeleton-${i}`} height={48} my={16} />
      ))}
    </Skeleton>
  );
};

interface StoreSelectModalProps {
  disableSubmit?: boolean;
  specialIsLoading: boolean;
  submitButtonType?: SUBMIT_BUTTON_VARIANTS;
  subtitle?: string;
}

export const StoreSelectorCard = ({
  specialIsLoading,
}: StoreSelectModalProps) => {
  const [disableBulkActions, setDisableBulkActions] = useState(false);
  const [storeSelectModalOpen, setStoreSelectModalOpen] = useState(false);
  const [showToggleOffWarning, setShowToggleOffWarning] = useState(false);

  const { data: storesData, isFetching: isFetchingStores } = useStores();

  const {
    setValue,
    clearErrors,
    setError,
    watch,
    formState: { errors },
  } = useFormContext();

  const storesWatch = watch('stores');

  const [showAll, setShowAll] = useState(false);

  const isTogglingAll = useRef(false);

  useEffect(() => {
    isTogglingAll.current = false;
  }, [storesWatch]);

  const stores = Object.values(storesWatch).filter((store: any) => store?.id);

  const storesForSelectModal = useMemo(
    () =>
      storesData?.map(({ id, ...restStore }) => ({
        id: `${id}`,
        ...restStore,
      })),
    [storesData]
  );

  const StoreIds = {
    asString: (id: number | string) => (typeof id === 'number' ? `${id}` : id),
    asNumber: (id: number | string) =>
      typeof id === 'string' ? parseInt(id, 10) : id,
  };

  const selectedStoreIds: string[] = (stores as GlobalSpecialStore[])
    .filter(({ can_edit, enabled }) => can_edit && enabled)
    .map((store: GlobalSpecialStore) => StoreIds.asString(store.id));

  const isStoreSelected = useCallback(
    (storeId: number | string) => {
      const foundStore: GlobalSpecialStore | undefined =
        storesWatch[StoreIds.asNumber(storeId)];
      return foundStore?.enabled || false;
    },
    [JSON.stringify(storesWatch)]
  );

  const numStoresSelected = (stores as GlobalSpecialStore[]).filter(
    (store: GlobalSpecialStore) => store.enabled
  ).length;

  const DEFAULT_STORES_IN_VIEW = 10;

  const handleSelectAll = (selected: boolean) => {
    setShowToggleOffWarning(!selected);

    Object.values(storesWatch as GlobalSpecialStore[]).forEach(
      (store: GlobalSpecialStore) => {
        setValue(`stores.${store.id}.enabled`, selected);
      }
    );
  };

  const handleShowAll = () => {
    setShowAll((prevShowAll) => !prevShowAll);
  };

  const storesInViewIndex = !showAll ? DEFAULT_STORES_IN_VIEW : stores.length;
  const storesInView = useMemo(
    () => (stores as GlobalSpecialStore[]).slice(0, storesInViewIndex),
    [stores, showAll]
  );

  const handleRowSelect = ({
    index,
    enabled,
  }: {
    enabled: boolean;
    index: number;
  }) => {
    if (isTogglingAll.current) return;
    setValue(`stores.${index}.enabled`, enabled);
  };

  const storesList = useMemo(() => {
    return (
      <>
        {storesInView.map((store: GlobalSpecialStore, index: number) => {
          const isChecked = isStoreSelected(store.id);
          if (!store.can_edit) {
            setDisableBulkActions(true);
          }
          const isLast =
            stores.length <= 10 && index === storesInView.length - 1;

          const storeId = StoreIds.asNumber(store.id);

          return (
            <StoreRow
              key={storeId}
              isLast={isLast}
              isDisabled={!store.can_edit}
              isChecked={isChecked}
              index={storeId}
              store={store}
              storeAddress={concatAddress(store)}
              handleChange={(enabled) =>
                handleRowSelect({ index: storeId, enabled })
              }
            />
          );
        })}

        {stores && stores.length > 10 && (
          <Flex alignItems="center" justifyContent="center" mt={24}>
            <Button
              mr={12}
              onClick={handleShowAll}
              label={`${showAll ? 'Show less' : 'Show all'}`}
              variant="primary"
            />
          </Flex>
        )}
      </>
    );
  }, [JSON.stringify(storesInView), isStoreSelected]);

  useEffect(() => {
    if (!(storesInView.length > 0)) {
      setError('store_ids', {
        message: 'No stores selected for special',
      });
    } else {
      clearErrors('store_ids');
    }
  }, [setError, JSON.stringify(storesInView)]);

  useEffect(() => {
    const allDisabled = Object.values(storesWatch)?.every(
      (store: any) => !store.enabled
    );

    setShowToggleOffWarning(allDisabled);
  }, [JSON.stringify(storesWatch)]);

  const onSelectStores = (
    storeSelections: { [storeId: number]: boolean },
    isDirty: boolean
  ) => {
    clearErrors();

    if (!isDirty) {
      setStoreSelectModalOpen(false);
      return;
    }

    const updatedStores = storesWatch;
    Object.keys(storeSelections).forEach((storeId: string) => {
      if (updatedStores[storeId]?.id) {
        updatedStores[storeId].enabled = storeSelections[parseInt(storeId)];
      } else if (storeSelections[parseInt(storeId)]) {
        const store = storesData?.find(
          (store) => store.id === parseInt(storeId)
        );
        updatedStores[storeId] = {
          enabled: true,
          can_edit: true,
          ...store,
        };
      }
    });

    setStoreSelectModalOpen(false);
    setValue('stores', updatedStores);
  };

  const storesSubtext = `Available at ${numStoresSelected} ${pluralize({
    noun: 'store',
    number: numStoresSelected,
    returnValue: 'word',
  })}`;

  return (
    <>
      <Card border="grays-light" width={MODAL_CARD_WIDTH} mb={32}>
        <Card.Content>
          <Flex
            mb={8}
            justifyContent="space-between"
            p={24}
            flexDirection="column"
          >
            {!!errors['stores']?.message && (
              <Banner
                full
                variant="error"
                icon={<AlertIcon />}
                label={errors['stores']?.message
                  ?.toString()
                  ?.replace(/stores/, '')}
                mt={16}
                mb={16}
              />
            )}
            <Flex justifyContent="space-between">
              <Flex mb={showToggleOffWarning ? 24 : 40} flexDirection="column">
                <Typography variant="h2-semibold">Stores</Typography>
                <Typography color="grays-mid">{storesSubtext}</Typography>
              </Flex>
              <Flex>
                {Object.keys(storesWatch).length > 1 && (
                  <>
                    <Button
                      mr={12}
                      onClick={() => handleSelectAll(true)}
                      label="Enable all"
                      variant="tertiary"
                      disabled={disableBulkActions}
                    />
                    <Button
                      mr={12}
                      onClick={() => handleSelectAll(false)}
                      label="Disable all"
                      variant="tertiary"
                      disabled={disableBulkActions}
                    />
                  </>
                )}
                <Button.Icon
                  icon={<EditIcon />}
                  onClick={() => setStoreSelectModalOpen(true)}
                  label="Edit stores"
                  variant="secondary"
                  data-testid="stores-edit"
                />
              </Flex>
            </Flex>
            {storesInView.length && showToggleOffWarning ? (
              <Banner
                icon={<InfoIcon />}
                label="Toggling off all stores will disable the special"
                onDismiss={() => setShowToggleOffWarning(false)}
                typography="body-regular"
                variant="info"
              />
            ) : null}
            {!specialIsLoading ? (
              <Flex
                flexDirection="column"
                mt={showToggleOffWarning ? 12 : 0}
                mb={32}
              >
                {storesList}
              </Flex>
            ) : (
              <StoreSelectorSkeleton />
            )}
          </Flex>
        </Card.Content>
      </Card>

      {storeSelectModalOpen && (
        <StoreSelectModal
          storesData={storesForSelectModal || []}
          isFetchingStores={isFetchingStores}
          onSubmit={onSelectStores}
          selectedStoreIds={selectedStoreIds}
          submitButtonType={SUBMIT_BUTTON_VARIANTS.select}
          closeModal={() => setStoreSelectModalOpen(false)}
        />
      )}
    </>
  );
};
